import React from 'react';
import SearchInput from './ui/SearchInput';
import { TOOLBAR_GROUP } from './ui/Toolbar';

/**
 * The shared order-list search box, used on every order-list page
 * (MyOrders / DepartmentOrders / Approvals) and in `MobileTabSearchRow`. The
 * caller owns the filtering; this component renders the box and lifts changes.
 *
 * 🔻 **ITS `Sort ▾` HALF IS DELETED (2026-10-18)** — pre-deletion SHA
 * `f499da97`. The dropdown existed for the order CARDS, which have no column
 * headers; the cards were retired (owner) and every caller already passed
 * `showSort={false}` wherever a table was showing. Every order list is a table
 * now and its column headers are the sort control. The file keeps its name
 * so no importer moved; renaming it is owed tidy-up.
 *
 * 🔑 The search box is the shared `components/ui/SearchInput`, so it is the
 * same size and the same look here as on the Breakdown and Manage Teams. The
 * owner's ruling is fixed, may shrink, never grows; the sizing lives once, in
 * `constants/inputs.js`. **Do not re-add a width or a `flex-1` here.**
 *
 * ⚠️ **The search box stays ON the bar and must never move into a popover.**
 * It is the one control whose current VALUE has to be readable without
 * opening anything (CLAUDE.md: *"Search is never a chip on any surface — it
 * has its own always-visible input"*).
 *
 * `debounceMs={0}` is deliberate and load-bearing: order search is 100%
 * client-side (`utils/orderSearch.js` `orderMatchesSearch` over an array the
 * page has already fetched), so any debounce is input lag protecting nothing.
 *
 * 🚨 `${prefix}-search-input` is a CONTRACT — client suites and
 * `e2e/orders.spec.js` select on it. Do not rename it.
 *
 * Props:
 * - `searchText` / `onSearchChange` — search input
 * - `searchPlaceholder` — input placeholder text. Default "Search orders…".
 * - `searchAriaLabel` — accessible name for the box (the placeholder alone is
 *   not one; it disappears the moment anything is typed).
 * - `testIdPrefix` — e.g. `"dept"`, `"approval"` → `${prefix}-search-input`.
 * - `showSearch` — default TRUE; `false` renders NOTHING (My Orders below `md`,
 *   where the box lives in the phone search row instead). Nothing rather than
 *   an empty `TOOLBAR_GROUP`: a childless flex item still takes a toolbar gap
 *   and can wrap onto a line of its own.
 * - `debounceMs` — opt-in (2026-10-02) for ONE caller: the Gag Breakdown's
 *   phone row, whose search REFETCHES `/api/gags` and so wants
 *   `ui/SearchInput`'s 300ms. Every order page omits it and keeps `0`.
 */
export default function OrderSearchAndSort({
  searchText,
  onSearchChange,
  searchPlaceholder = 'Search orders…',
  searchAriaLabel = 'Search orders',
  testIdPrefix = 'orders',
  showSearch = true,
  debounceMs = 0,
}) {
  if (!showSearch) return null;
  return (
    <div className={TOOLBAR_GROUP}>
      <SearchInput
        value={searchText}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        ariaLabel={searchAriaLabel}
        debounceMs={debounceMs}
        testId={`${testIdPrefix}-search-input`}
      />
    </div>
  );
}
